// src/features/ranking/services/rankingService.ts

import api from '../../../api/axios';

import {
  HISTORY_ENDPOINTS,
  RANKING_ENDPOINTS,
  STORE_ENDPOINTS,
} from '../../../api';

import type {
  ApiResponse,
} from '../../../shared/types/api.types';

import type {
  HistoryResponse,
} from '../../history/types/history.types';

import type {
  GetScoreHistoryRequest,
  RankingData,
  StoreUserScore,
} from '../types/ranking.types';


export const rankingService = {
  /**
   * =========================================================
   * 매장 월간 승점 랭킹 조회
   *
   * USER / ADMIN / SUPERADMIN
   *
   * GET /api/v2/rankings/stores/{storeId}
   * =========================================================
   */
  async getStoreRanking(
    storeId: number,
    month?: string,
  ): Promise<RankingData> {
    const response =
      await api.get<
        ApiResponse<RankingData>
      >(
        RANKING_ENDPOINTS
          .STORE_RANKING(
            storeId,
          ),
        {
          params: {
            month,
          },
        },
      );

    return (
      response.data.data ?? {
        storeId,
        rankings: [],
      }
    );
  },


  /**
   * =========================================================
   * 내 전체 매장 승점 조회
   *
   * USER / ADMIN / SUPERADMIN
   *
   * GET /api/v2/rankings/me
   * =========================================================
   */
  async getMyScoreList():
    Promise<StoreUserScore[]> {
    const response =
      await api.get<
        ApiResponse<StoreUserScore[]>
      >(
        RANKING_ENDPOINTS
          .MY_SCORES,
      );

    return (
      response.data.data ??
      []
    );
  },


  /**
   * =========================================================
   * 내 특정 매장 승점 조회
   *
   * USER / ADMIN / SUPERADMIN
   *
   * GET /api/v2/rankings/me/stores/{storeId}
   * =========================================================
   */
  async getMyStoreScore(
    storeId: number,
  ): Promise<StoreUserScore | null> {
    const response =
      await api.get<
        ApiResponse<StoreUserScore>
      >(
        RANKING_ENDPOINTS
          .MY_STORE_SCORE(
            storeId,
          ),
      );

    return (
      response.data.data ??
      null
    );
  },


  /**
   * =========================================================
   * 회원 승점 변경
   *
   * ADMIN / SUPERADMIN
   *
   * PATCH /api/v2/admin/users/{userId}/scores
   * =========================================================
   */
  async updateUserScore(
    userId: string,
    score: number,
    storeId: number,
  ): Promise<void> {
    await api.patch(
      RANKING_ENDPOINTS
        .ADMIN
        .USER_SCORES(
          userId,
        ),
      {
        score,
        storeId,
      },
    );
  },


  /**
   * =========================================================
   * 회원 승점 히스토리 조회
   *
   * ADMIN / SUPERADMIN
   *
   * GET /api/v2/admin/users/{userId}/histories/scores
   * =========================================================
   */
  async getUserScoreHistory({
    userId,
    storeId,
    startDate,
    endDate,
    page,
    size,
  }: GetScoreHistoryRequest):
    Promise<HistoryResponse> {
    const response =
      await api.get<
        ApiResponse<HistoryResponse>
      >(
        HISTORY_ENDPOINTS
          .ADMIN
          .USER_SCORES(
            userId,
          ),
        {
          params: {
            storeId,
            startDate,
            endDate,
            page,
            size,
          },
        },
      );

    return (
      response.data.data ?? {
        histories: [],
        totalCount: 0,
      }
    );
  },


  /**
   * =========================================================
   * 회원 현재 월 매장별 승점 조회
   *
   * ADMIN / SUPERADMIN
   *
   * GET /api/v2/admin/users/{userId}/scores
   * =========================================================
   */
  async getUserScoreList(
    userId: string,
  ): Promise<StoreUserScore[]> {
    const response =
      await api.get<
        ApiResponse<StoreUserScore[]>
      >(
        RANKING_ENDPOINTS
          .ADMIN
          .USER_SCORES(
            userId,
          ),
      );

    return (
      response.data.data ??
      []
    );
  },


  /**
   * =========================================================
   * 매장 승점 전체 초기화
   *
   * ADMIN
   * - 본인 매장
   *
   * SUPERADMIN
   * - 모든 매장
   *
   * PATCH /api/v2/admin/stores/{storeId}/scores/reset
   * =========================================================
   */
  async resetStoreScore(
    storeId: number,
  ): Promise<void> {
    await api.patch(
      STORE_ENDPOINTS
        .ADMIN
        .RESET_SCORES(
          storeId,
        ),
    );
  },
};